import { CartAddEvent } from '@theme/events';
import { lineDeliveryProperties, syncCartDeliveryLanguage } from '@theme/arc-line-delivery';

class ArcCartUpsell extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('change', (event) => {
      const select = event.target.closest('select');
      if (!select?.value) return;
      const option = select.selectedOptions[0];
      const price = select.closest('article').querySelector('.arc-cart-upsell-price');
      price.replaceChildren(document.createTextNode(option.dataset.price));
      if (option.dataset.compare) {
        const original = document.createElement('s');
        original.textContent = option.dataset.compare;
        price.append(' ', original);
      }
    }, { signal: this.controller.signal });
    this.addEventListener('submit', (event) => this.add(event), { signal: this.controller.signal });
  }

  disconnectedCallback() { this.controller?.abort(); }

  async add(event) {
    const form = event.target.closest('.arc-cart-upsell-form');
    if (!form) return;
    event.preventDefault();
    if (this.busy || !form.reportValidity()) return;
    const option = form.querySelector('select').selectedOptions[0];
    const button = form.querySelector('button');
    const error = this.querySelector('[role="alert"]');
    this.busy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    error.hidden = true;
    try {
      const data = new FormData(form);
      data.set('quantity', '1');
      const currentCartResponse = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!currentCartResponse.ok) throw new Error('Cart could not be read');
      const currentCart = await currentCartResponse.json();
      const currentQuantity = currentCart.items.reduce((total, item) => String(item.variant_id) === option.value ? total + item.quantity : total, 0);
      const properties = lineDeliveryProperties({
        shipping: option.dataset.shipping === 'true',
        stock: option.dataset.stock === '' ? null : Number(option.dataset.stock),
        quantity: 1,
        cartQuantity: currentQuantity,
      });
      for (const [key, value] of Object.entries(properties || {})) data.set(`properties[${key}]`, value);
      data.set('sections', [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), e => e.dataset.sectionId))].slice(0, 5).join(','));
      data.set('sections_url', window.location.pathname);
      const response = await fetch(this.dataset.addUrl, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error('Cart add failed');
      const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const cart = cartResponse.ok ? await cartResponse.json() : {};
      this.dispatchEvent(new CartAddEvent(cart, 'arc-cart-upsell', { source: 'arc-cart-upsell', itemCount: cart.item_count ?? Number(this.dataset.count) + 1, sections: result.sections }));
    } catch {
      error.hidden = false;
    } finally {
      this.busy = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('arc-cart-upsell')) customElements.define('arc-cart-upsell', ArcCartUpsell);

const deliveryCheckoutReady = new WeakSet();
const deliveryCheckoutPending = new WeakSet();
document.addEventListener('submit', async (event) => {
  const form = event.target;
  const button = event.submitter;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form' || button?.name !== 'checkout') return;
  if (deliveryCheckoutReady.has(form)) {
    deliveryCheckoutReady.delete(form);
    return;
  }
  event.preventDefault();
  if (deliveryCheckoutPending.has(form)) return;
  deliveryCheckoutPending.add(form);
  button.setAttribute('aria-busy', 'true');
  const previousError = form.querySelector('[data-arc-delivery-sync-error]');
  previousError?.remove();
  try {
    await syncCartDeliveryLanguage();
    deliveryCheckoutReady.add(form);
    form.requestSubmit(button);
  } catch {
    const error = document.createElement('p');
    error.dataset.arcDeliverySyncError = '';
    error.setAttribute('role', 'alert');
    error.textContent = document.documentElement.lang.startsWith('fi')
      ? 'Toimitustiedon päivitys ei onnistunut. Yritä siirtyä kassalle uudelleen.'
      : 'The delivery details could not be updated. Please try checking out again.';
    form.append(error);
  } finally {
    deliveryCheckoutPending.delete(form);
    button.removeAttribute('aria-busy');
  }
}, true);
